import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { CompteResultat, SituationPatrimoniale, StatVentes } from '../models/models';
import api from './api';

// Sous-état d'un rapport (chargé indépendamment des autres).
interface ReportState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface StatistiqueState {
  compteResultat: ReportState<CompteResultat>;
  situationPatrimoniale: ReportState<SituationPatrimoniale>;
  statVentes: ReportState<StatVentes>;
}

const initialState: StatistiqueState = {
  compteResultat:        { data: null, loading: false, error: null },
  situationPatrimoniale: { data: null, loading: false, error: null },
  statVentes:            { data: null, loading: false, error: null },
};

// Filtres des stats de vente — tous optionnels sauf la période.
export interface StatVentesFilters {
  type_doc_code?: string | null;
  tiers_id?: number | null;
  article_id?: number | null;
  famille_id?: number | null;
  depot_id?: number | null;
  departement_id?: number | null;   // n'a de sens qu'en mode « tous départements »
  date_debut: string;
  date_fin: string;
}

// ── Compte de résultat (sur une période) ──────────────────────────────────────
export const fetchCompteResultat = createAsyncThunk<
  CompteResultat,
  { date_debut: string; date_fin: string },
  { rejectValue: string }
>(
  'statistique/fetchCompteResultat',
  async ({ date_debut, date_fin }, thunkAPI) => {
    try {
      const response = await api.get('/statistique/compteResultat', {
        params: { date_debut, date_fin },
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error || error.response?.data?.message || 'Erreur lors du chargement du compte de résultat.'
      );
    }
  }
);

// ── Situation patrimoniale (à une date) ───────────────────────────────────────
export const fetchSituationPatrimoniale = createAsyncThunk<
  SituationPatrimoniale,
  { date: string },
  { rejectValue: string }
>(
  'statistique/fetchSituationPatrimoniale',
  async ({ date }, thunkAPI) => {
    try {
      const response = await api.get('/statistique/situationPatrimoniale', { params: { date } });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error || error.response?.data?.message || 'Erreur lors du chargement de la situation patrimoniale.'
      );
    }
  }
);

// ── Stats de vente (filtrables) ───────────────────────────────────────────────
export const fetchStatVentes = createAsyncThunk<
  StatVentes,
  StatVentesFilters,
  { rejectValue: string }
>(
  'statistique/fetchStatVentes',
  async (filters, thunkAPI) => {
    try {
      const response = await api.get('/statistique/ventes', { params: filters });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error || error.response?.data?.message || 'Erreur lors du chargement des stats de vente.'
      );
    }
  }
);

const statistiqueSlice = createSlice({
  name: 'statistique',
  initialState,
  reducers: {
    clearCompteResultat: (state) => {
      state.compteResultat = { data: null, loading: false, error: null };
    },
    clearSituationPatrimoniale: (state) => {
      state.situationPatrimoniale = { data: null, loading: false, error: null };
    },
    clearStatVentes: (state) => {
      state.statVentes = { data: null, loading: false, error: null };
    },
  },
  extraReducers: (builder) => {
    builder
      // Compte de résultat
      .addCase(fetchCompteResultat.pending, (s) => { s.compteResultat.loading = true; s.compteResultat.error = null; })
      .addCase(fetchCompteResultat.fulfilled, (s, a) => { s.compteResultat.loading = false; s.compteResultat.data = a.payload; })
      .addCase(fetchCompteResultat.rejected, (s, a) => { s.compteResultat.loading = false; s.compteResultat.error = a.payload as string; })
      // Situation patrimoniale
      .addCase(fetchSituationPatrimoniale.pending, (s) => { s.situationPatrimoniale.loading = true; s.situationPatrimoniale.error = null; })
      .addCase(fetchSituationPatrimoniale.fulfilled, (s, a) => { s.situationPatrimoniale.loading = false; s.situationPatrimoniale.data = a.payload; })
      .addCase(fetchSituationPatrimoniale.rejected, (s, a) => { s.situationPatrimoniale.loading = false; s.situationPatrimoniale.error = a.payload as string; })
      // Stats de vente
      .addCase(fetchStatVentes.pending, (s) => { s.statVentes.loading = true; s.statVentes.error = null; })
      .addCase(fetchStatVentes.fulfilled, (s, a) => { s.statVentes.loading = false; s.statVentes.data = a.payload; })
      .addCase(fetchStatVentes.rejected, (s, a) => { s.statVentes.loading = false; s.statVentes.error = a.payload as string; });
  },
});

export const { clearCompteResultat, clearSituationPatrimoniale, clearStatVentes } = statistiqueSlice.actions;
export default statistiqueSlice.reducer;
